import { useMemo } from 'react';
import { useTourStore } from '@/state/tour';
import { useHeroStore } from '@/store/store';
import { clockText, instant, localMinutes } from './clock';
import { SITE, useWorldStore } from './state';
import { sunPosition, sunTimes } from './sun';

// beyond this the file's IfcSite is somewhere else than the site the neighbours were baked for
const SAME_SITE_KM = 2;

const deg = (v: number, pos: string, neg: string) =>
  `${Math.abs(v).toFixed(4)}° ${v >= 0 ? pos : neg}`;
const latLon = (lat: number, lon: number) => `${deg(lat, 'N', 'S')}, ${deg(lon, 'E', 'W')}`;

function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const r = Math.PI / 180;
  const h =
    Math.sin(((b.lat - a.lat) * r) / 2) ** 2 +
    Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(((b.lon - a.lon) * r) / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(h));
}

function IfcSiteLine() {
  const status = useHeroStore((s) => s.status);
  const site = useHeroStore((s) => s.site);
  const origin = useWorldStore((s) => s.origin);
  if (status !== 'ready') return <p className="world-line muted">IfcSite: reading the file…</p>;
  const north =
    site?.trueNorth != null
      ? `true north ${site.trueNorth.toFixed(1)}° from project north`
      : 'no true north, so project north is north';
  if (site?.latitude == null || site.longitude == null)
    return (
      <p className="world-line muted">
        IfcSite has no location; placed at the pipeline&apos;s coordinates, {north}.
      </p>
    );
  const here = { lat: site.latitude, lon: site.longitude };
  const km = distanceKm(here, origin);
  if (km <= SAME_SITE_KM)
    return <p className="world-line muted">IfcSite matches the site, {north}.</p>;
  return (
    <p className="world-line muted">
      IfcSite says {latLon(here.lat, here.lon)}, {Math.round(km).toLocaleString('en')} km away; I
      use the pipeline&apos;s coordinates instead, {north}.
    </p>
  );
}

export function WorldPanel() {
  const date = useWorldStore((s) => s.date);
  const minutes = useWorldStore((s) => s.minutes);
  const origin = useWorldStore((s) => s.origin);
  const source = useWorldStore((s) => s.source);
  const height = useWorldStore((s) => s.height);
  const compass = useWorldStore((s) => s.compass);
  const setDate = useWorldStore((s) => s.setDate);
  const setMinutes = useWorldStore((s) => s.setMinutes);
  const reduced = useTourStore((s) => s.reducedMotion);
  const tz = SITE.timeZone;

  const { rise, set } = useMemo(() => {
    const t = sunTimes(origin.lat, origin.lon, instant(date, 12 * 60, tz));
    return {
      rise: t.sunrise ? localMinutes(t.sunrise, date, tz) : 0,
      set: t.sunset ? localMinutes(t.sunset, date, tz) : 24 * 60,
    };
  }, [date, origin, tz]);
  const sun = sunPosition(origin.lat, origin.lon, instant(date, minutes, tz));
  const shadow =
    sun.altitude > 0.5 && height > 0 ? height / Math.tan((sun.altitude * Math.PI) / 180) : null;
  const clamped = Math.min(set, Math.max(rise, minutes));

  return (
    <div className="lens-panel world">
      <div className="lens-bar">
        <span className="lens-dot" />
        <span>sun and site</span>
        <span className="lens-right">{source === 'osm' ? 'Innsbruck · OSM' : 'Innsbruck'}</span>
      </div>
      <div className="world-controls">
        <input
          type="date"
          value={date}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          aria-label="Date"
        />
        <input
          type="range"
          min={Math.ceil(rise / 5) * 5}
          max={Math.floor(set / 5) * 5}
          step={5}
          value={clamped}
          onChange={(e) => setMinutes(Number(e.target.value), false)}
          aria-label="Time of day"
        />
        <span className="world-clock">{clockText(clamped)}</span>
      </div>
      <dl className="lens-stats">
        <div>
          <dt>sunrise</dt>
          <dd>{clockText(rise)}</dd>
        </div>
        <div>
          <dt>sunset</dt>
          <dd>{clockText(set)}</dd>
        </div>
        <div>
          <dt>altitude</dt>
          <dd>{sun.altitude.toFixed(0)}°</dd>
        </div>
        <div>
          <dt>azimuth</dt>
          <dd>{sun.azimuth.toFixed(0)}°</dd>
        </div>
      </dl>
      <p className="world-line">
        <span className="compass" title="true north on screen" aria-hidden>
          <span className="needle" style={{ transform: `rotate(${compass.toFixed(0)}deg)` }}>
            N
          </span>
        </span>
        {latLon(origin.lat, origin.lon)}
      </p>
      <p className="world-line">
        {shadow != null
          ? `Roof shadow on the ground: ${shadow.toFixed(1)} m (roof ${height.toFixed(1)} m high)`
          : 'Sun below the horizon'}
      </p>
      <IfcSiteLine />
      <p className="lens-foot">
        {source === 'placeholder'
          ? 'placeholder neighbours, run pnpm pipeline neighbours'
          : '© OpenStreetMap contributors, ODbL'}
        {reduced ? '' : ' · the scroll runs the day until you move the slider'}
      </p>
    </div>
  );
}
